// ADV-MKT-SIGNAL-BUYDEF-001 — fail-closed BUY CANDIDATE signal ladder.
//
// BUY CANDIDATE is a COMPUTED, fail-closed setup state. It may fire only when
// every gate below passes. The headline invariant is absolute:
//   a row may NEVER be BUY CANDIDATE while price < breakout_trigger.
// Any required field that is missing / unknown / null / malformed forces a
// demotion (never BUY CANDIDATE). This module is the single source of the
// ladder; the Markets board applies it to rows the backend currently labels a
// buy candidate (signal_use === watchlist_candidate). All other signal_use
// values keep their existing PREDEPLOY-001 SIGNAL_POLICY mapping unchanged.

// Max distance above the breakout trigger still eligible for BUY CANDIDATE.
// Config-backed / centralized here (NOT a scattered magic number).
// PENDING DARREN/NOVA CALIBRATION.
export const BUY_CANDIDATE_MAX_EXTENSION_PCT = 0.03;

export type LadderState =
  | 'DO_NOT_ACT'
  | 'HIGH_RISK'
  | 'REVIEW_REQUIRED'
  | 'BUY_CANDIDATE'
  | 'APPROACHING_BREAKOUT'
  | 'WATCHLIST_CANDIDATE';

export interface LadderInput {
  price?: number | string | null;
  breakout_trigger?: number | string | null;
  integrity_band?: string | null;
  integrity_status?: string | null;
  integrity_decision_band?: string | null;
  catalyst_status?: string | null;
  dq_flags?: string[] | null;
  calc_mismatch?: boolean | null;
  instrument_class?: string | null;
  bias?: string | null;
  direction?: string | null;
  risk_band?: string | null;
  quote_freshness?: string | null;   // ADV-MKT-BUY-CANDIDATE-CONTRACT-001: align staleness with boardActionability
  data_quality?: string | null;
}

export interface LadderResult {
  state: LadderState;
  label: string;
  reason: string;
}

export interface LadderConfig { maxExtensionPct: number; }

function _num(v: any): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : null;
}
function _up(v: any): string { return String(v === null || v === undefined ? '' : v).toUpperCase().trim(); }
function _money(n: number): string {
  return '$' + (Number.isFinite(n) ? (n >= 100 ? n.toFixed(2) : n.toFixed(2)) : String(n));
}

const _STATE_LABEL: Record<LadderState, string> = {
  DO_NOT_ACT: 'DO NOT ACT',
  HIGH_RISK: 'HIGH RISK',
  REVIEW_REQUIRED: 'REVIEW REQUIRED',
  BUY_CANDIDATE: 'BUY CANDIDATE',
  APPROACHING_BREAKOUT: 'APPROACHING BREAKOUT',
  WATCHLIST_CANDIDATE: 'WATCHLIST CANDIDATE',
};

// First-match-wins fail-closed ladder for a long/bullish candidate row.
export function resolveBuyCandidate(
  r: LadderInput,
  cfg: LadderConfig = { maxExtensionPct: BUY_CANDIDATE_MAX_EXTENSION_PCT },
): LadderResult {
  const out = (state: LadderState, reason: string): LadderResult => ({ state, label: _STATE_LABEL[state], reason });

  const price = _num(r.price);
  const breakout = _num(r.breakout_trigger);
  const dq = (Array.isArray(r.dq_flags) ? r.dq_flags : []).map(_up);
  const dqHas = (k: string) => dq.indexOf(k) !== -1;
  const band = _up(r.integrity_band);
  const istatus = _up(r.integrity_status);
  // ADV-MKT-BUY-CANDIDATE-CONTRACT-001 — canonical integrity DECISION band.
  // Prefer the backend's integrity_decision_band when present; otherwise derive it the
  // SAME way the backend does (red_flag_scorer.decision_band): CLEAR iff the raw band is
  // CLEAR AND facts are SCORED, else REVIEW_REQUIRED. The live backend currently emits
  // integrity_band + integrity_status but NOT integrity_decision_band (the running
  // service predates BUYGATE-FAILCLOSED-001), so without this fallback the gate could
  // never be satisfied and BUY CANDIDATE could never fire. Fail-closed: a raw CLEAR band
  // with PARTIAL/INSUFFICIENT facts is NOT a CLEAR decision (does not weaken scoring).
  const decisionBandRaw = _up(r.integrity_decision_band);
  const decisionBand = decisionBandRaw || (band === 'CLEAR' && istatus === 'SCORED' ? 'CLEAR' : 'REVIEW_REQUIRED');
  const cat = _up(r.catalyst_status);
  const ic = String(r.instrument_class || '').toLowerCase().trim();
  const bias = String(r.bias || '').toLowerCase().trim();
  const riskBand = _up(r.risk_band);
  const belowReason = (breakout !== null && price !== null && price < breakout)
    ? ' below breakout ' + _money(breakout)
    : '';

  // 1. DO NOT ACT — active data-quality blocker / stale / mismatch.
  // ADV-MKT-BUY-CANDIDATE-CONTRACT-001: also treat a stale quote_freshness/data_quality
  // string as stale (e.g. "stale_market_closed" after hours), mirroring boardActionability
  // ._isStale — otherwise the BUY chip could disagree with the row's Stale category.
  const qf = String(r.quote_freshness || r.data_quality || '').toLowerCase();
  if (dqHas('STALE') || dqHas('QUOTE_STALE') || qf.includes('stale')) return out('DO_NOT_ACT', 'stale quote');
  if (dqHas('FEED_DIVERGENCE')) return out('DO_NOT_ACT', 'feed divergence');
  if (dqHas('CALC_MISMATCH') || r.calc_mismatch === true) return out('DO_NOT_ACT', 'calculation mismatch');

  // 2. HIGH RISK / AVOID — integrity band or risk band.
  if (band === 'HIGH_RISK' || band === 'DO_NOT_TRADE' || riskBand === 'DO_NOT_TRADE') {
    return out('HIGH_RISK', 'integrity ' + (band || riskBand).toLowerCase().replace(/_/g, ' '));
  }

  // 3. REVIEW REQUIRED — eligibility / verification blockers (still reviewable).
  if (ic === 'leveraged_inverse') return out('REVIEW_REQUIRED', 'inverse leveraged instrument' + belowReason);
  if (bias && bias !== 'bullish') return out('REVIEW_REQUIRED', 'bias not bullish' + belowReason);
  if (cat !== 'CATALYST_VERIFIED' && cat !== 'VERIFIED') {
    const c = cat.indexOf('INVESTIG') !== -1 || cat.indexOf('PENDING') !== -1 || cat === 'CATALYST_CHECK_PENDING'
      ? 'catalyst investigating'
      : (cat.indexOf('ENTITY') !== -1 ? 'entity unconfirmed' : 'missing catalyst verification');
    return out('REVIEW_REQUIRED', c + belowReason);
  }
  if (decisionBand !== 'CLEAR') {
    const ireason = istatus === 'INSUFFICIENT_FACTS' ? 'integrity INSUFFICIENT'
      : istatus === 'PARTIAL_FACTS' ? 'integrity PARTIAL'
      : band === 'CAUTION' ? 'integrity CAUTION'
      : decisionBand ? 'decision band ' + decisionBand.toLowerCase().replace(/_/g, ' ')
      : 'integrity decision band missing';
    return out('REVIEW_REQUIRED', ireason + belowReason);
  }
  if (ic !== 'standard' && ic !== 'leveraged_long' && ic !== '') {
    return out('REVIEW_REQUIRED', 'ineligible instrument' + belowReason);
  }

  // Past here: bullish, integrity CLEAR, catalyst VERIFIED, dq empty, eligible instrument.
  // 4/5/6 gate on the breakout trigger (fail-closed if absent).
  if (breakout === null) return out('REVIEW_REQUIRED', 'missing breakout trigger');
  if (price === null) return out('REVIEW_REQUIRED', 'missing price');

  const cap = breakout * (1 + (cfg.maxExtensionPct ?? BUY_CANDIDATE_MAX_EXTENSION_PCT));
  if (price < breakout) return out('APPROACHING_BREAKOUT', 'below breakout ' + _money(breakout));
  if (price > cap) {
    const ext = ((price - breakout) / breakout) * 100;
    return out('WATCHLIST_CANDIDATE', 'extended ' + ext.toFixed(1) + '% above breakout; waiting for cleaner setup');
  }
  // price >= breakout && price <= breakout*(1+ext): all gates pass.
  return out('BUY_CANDIDATE', 'at/above breakout ' + _money(breakout) + ', integrity clear, catalyst verified');
}

export default resolveBuyCandidate;
